import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, CircularProgress, Box, Typography } from "@mui/material";
import { ClinicalBudgetProvider } from "shared/providers/ClinicalBudget";
import { ClinicalBudgetStep } from "components/ClinicalBudget/components/stepper";
import { useAuth } from "shared/providers/auth";
import { loadOrcamento } from "requests/ClinicalBudget";
import { IBudgetItem } from "shared/types/ClinicalBudget";
import toast from "react-hot-toast";

const ClinicalBudgetEdit: React.FC = () => {
  const { idOrcamento } = useParams<{ idOrcamento: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string>("");
  const [orcData, setOrcData] = React.useState<{
    id: number;
    items: IBudgetItem[];
    desconto: string;
    entrada: string;
    parcelas: number;
    dataVencimento: string;
    obs: string;
    pacId: number;
  } | null>(null);

  const idOrc = parseInt(idOrcamento || "0", 10);

  React.useEffect(() => {
    if (idOrc > 0) {
      fetchOrcamento();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idOrc]);

  const fetchOrcamento = async () => {
    setLoading(true);
    try {
      const userId = user?.data?.id || "";
      const { data, status } = await loadOrcamento(userId, idOrc);

      if ([200, 201].includes(status) && data.success && data.data) {
        const orc = data.data.orcamento;
        const itens = data.data.itens;
        const dentistaFazOrc = data.data.dentista_faz_orc ?? 0;
        const pacienteApi = data.data.paciente;

        const formatMoeda = (valor: number): string => {
          return `R$ ${valor.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
        };

        const personAttendance = localStorage.getItem("personAttendance");
        let pacId = orc.pac_id || 0;
        let pacName = pacienteApi?.nome || "";

        if (personAttendance) {
          const parsed = JSON.parse(personAttendance);
          pacId = pacId || parsed.pac_id || 0;
          if (!pacName) pacName = parsed.pac_name || "";
          parsed.pac_id = pacId;
          parsed.pac_name = pacName;
          parsed.dentista_faz_orc = dentistaFazOrc;
          localStorage.setItem("personAttendance", JSON.stringify(parsed));
        } else {
          localStorage.setItem("personAttendance", JSON.stringify({
            pac_id: pacId,
            pac_name: pacName,
            dentista_faz_orc: dentistaFazOrc,
          }));
        }

        setOrcData({
          id: orc.id,
          items: itens.map((item) => ({
            ...item,
            id: item.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          })),
          desconto: formatMoeda(orc.vl_desconto),
          entrada: formatMoeda(orc.vl_entrada),
          parcelas: orc.parcelas,
          dataVencimento: orc.dt_vencimento || "",
          obs: orc.obs || "",
          pacId,
        });
      } else {
        setError(data?.message || "Erro ao carregar orçamento.");
        toast.error(data?.message || "Erro ao carregar orçamento.");
      }
    } catch (err) {
      console.error("Erro ao carregar orçamento:", err);
      setError("Erro ao carregar orçamento. Tente novamente.");
      toast.error("Erro ao carregar orçamento.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="textSecondary">Carregando orçamento...</Typography>
      </Box>
    );
  }

  if (error || !orcData) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
          padding: 3,
          textAlign: "center",
        }}
      >
        <Typography color="error" variant="h6">
          {error || "Orçamento não encontrado."}
        </Typography>
        <Typography
          color="primary"
          sx={{ cursor: "pointer", textDecoration: "underline" }}
          onClick={() => navigate(-1)}
        >
          Voltar
        </Typography>
      </Box>
    );
  }

  return (
    <ClinicalBudgetProvider
      editMode={true}
      editOrcamentoId={orcData.id}
      initialItems={orcData.items}
      initialDesconto={orcData.desconto}
      initialEntrada={orcData.entrada}
      initialParcelas={orcData.parcelas}
      initialDataVencimento={orcData.dataVencimento}
      initialObs={orcData.obs}
    >
      <Container maxWidth="xl" sx={{ p: 0 }}>
        <ClinicalBudgetStep />
      </Container>
    </ClinicalBudgetProvider>
  );
};

export { ClinicalBudgetEdit };
